/**
 * useRecording — session recording hook.
 *
 * Owns the MediaRecorder lifecycle, audio-graph taps, and the 1Hz MM:SS timer.
 * Caller supplies an array of `taps` (named getters of AudioNodes that should
 * be captured) plus an optional `setupExtraTaps` callback for connections
 * with custom build/teardown logic (e.g. live-audio with 8D routing).
 *
 * The hook deliberately stays out of UX territory — it returns a raw WebM Blob
 * from `stopRecording()` and lets the parent decide how to encode/download it.
 * That keeps platform-specific download logic + rich toast JSX in the
 * component where they belong.
 *
 * Usage:
 *   const rec = useRecording({ audioContextRef });
 *   await rec.startRecording({
 *     taps: [
 *       { name: 'beats',      getNode: () => gainNodeRef.current },
 *       { name: 'background', getNode: () => bgEnabled ? bgGainRef.current : null },
 *       { name: 'synth-bus',  getNode: () => synthBusRef.current },
 *     ],
 *     setupExtraTaps: (dest) => {
 *       // Wire any complex graphs (returns cleanup fn or void)
 *       return () => { ... };
 *     },
 *   });
 *   ...
 *   const blob = await rec.stopRecording();
 *
 * State exposed:
 *   isRecording, recordingElapsed (seconds), formattedDuration ('MM:SS')
 */
import { useCallback, useRef, useState } from 'react';

const _DEFAULT_OPTIONS = {
  // 510 kbps Opus = "fullband" mode, preserves frequencies up to 20 kHz.
  // Critical for binaural fidelity — anything below 64 kbps drops into
  // wideband (8 kHz cutoff) and high carriers turn into mush.
  audioBitsPerSecond: 510000,
  mimeType: 'audio/webm;codecs=opus',
  // MediaRecorder.start() interval in ms. 100ms = many small chunks → less
  // memory pressure than 1s chunks for long sessions, no quality difference.
  timesliceMs: 100,
};

const _formatMMSS = (sec) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;

export function useRecording({ audioContextRef, options = {} }) {
  const opts = { ..._DEFAULT_OPTIONS, ...options };

  const [isRecording, setIsRecording] = useState(false);
  const [recordingElapsed, setRecordingElapsed] = useState(0);

  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingDestinationRef = useRef(null);
  // List of {node, name} pairs we connected so stopRecording can undo them.
  const activeTapsRef = useRef([]);
  // Cleanup fn returned by setupExtraTaps (live audio etc).
  const extraTapCleanupRef = useRef(null);

  const startTimeRef = useRef(null);
  const tickRef = useRef(null);

  const _stopTick = () => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    startTimeRef.current = null;
  };

  const _disconnectAllTaps = () => {
    const dest = recordingDestinationRef.current;
    if (dest) {
      activeTapsRef.current.forEach(({ node, name }) => {
        try { node.disconnect(dest); }
        catch (e) { console.warn(`Recording cleanup: ${name} disconnect`, e); }
      });
    }
    activeTapsRef.current = [];

    if (extraTapCleanupRef.current) {
      try { extraTapCleanupRef.current(); }
      catch (e) { console.warn('Recording cleanup: extra-tap cleanup', e); }
      extraTapCleanupRef.current = null;
    }
  };

  const startRecording = useCallback(async ({ taps = [], setupExtraTaps = null } = {}) => {
    const ctx = audioContextRef.current;
    if (!ctx) throw new Error('AudioContext not initialized');

    // Resume if browser auto-suspended (sleep, tab switch, etc).
    if (ctx.state === 'suspended') await ctx.resume();
    if (ctx.state !== 'running') {
      throw new Error(`AudioContext is ${ctx.state}, cannot record`);
    }

    // Build the recording destination + wire taps.
    const dest = ctx.createMediaStreamDestination();
    recordingDestinationRef.current = dest;
    activeTapsRef.current = [];

    taps.forEach(({ name, getNode }) => {
      const node = getNode();
      if (!node) return;
      try {
        node.connect(dest);
        activeTapsRef.current.push({ node, name });
        console.log(`✅ Recording tap connected: ${name}`);
      } catch (e) {
        console.warn(`⚠️ Recording tap '${name}' failed to connect:`, e);
      }
    });

    if (typeof setupExtraTaps === 'function') {
      try {
        const cleanup = setupExtraTaps(dest);
        if (typeof cleanup === 'function') extraTapCleanupRef.current = cleanup;
      } catch (e) {
        console.warn('setupExtraTaps threw:', e);
      }
    }

    // Sanity check — at least one track must exist or encoding fails silently.
    if (dest.stream.getTracks().length === 0) {
      throw new Error('Recording stream has no audio tracks (no taps connected)');
    }

    // Pick a supported mime type.
    let mimeType = opts.mimeType;
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = '';
    }

    const recorder = new MediaRecorder(dest.stream, {
      mimeType,
      audioBitsPerSecond: opts.audioBitsPerSecond,
    });
    recordedChunksRef.current = [];

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        recordedChunksRef.current.push(event.data);
      }
    };
    recorder.onerror = (event) => {
      console.error('MediaRecorder error:', event.error);
    };

    mediaRecorderRef.current = recorder;
    recorder.start(opts.timesliceMs);

    // Timer starts only after recorder is verified running.
    startTimeRef.current = Date.now();
    setRecordingElapsed(0);
    setIsRecording(true);
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      if (startTimeRef.current) {
        setRecordingElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }
    }, 1000);

    console.log(`🎙️ Recording started: ${mimeType || 'browser default'} @ ${opts.audioBitsPerSecond / 1000} kbps, ${activeTapsRef.current.length} tap(s)`);
  }, [audioContextRef, opts.audioBitsPerSecond, opts.mimeType, opts.timesliceMs]);

  /**
   * Stop the recorder and resolve to the assembled WebM Blob.
   * Caller is responsible for encoding (MP3) and download UX.
   */
  const stopRecording = useCallback(() => {
    return new Promise((resolve, reject) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        reject(new Error('No active recording'));
        return;
      }

      recorder.onstop = () => {
        try {
          const blob = new Blob(recordedChunksRef.current, { type: 'audio/webm' });
          _disconnectAllTaps();
          recordingDestinationRef.current = null;
          mediaRecorderRef.current = null;
          recordedChunksRef.current = [];
          _stopTick();
          setIsRecording(false);
          setRecordingElapsed(0);
          console.log(`📼 Recording stopped: ${(blob.size / 1024 / 1024).toFixed(2)} MB`);
          resolve(blob);
        } catch (e) {
          reject(e);
        }
      };

      try { recorder.stop(); }
      catch (e) { reject(e); }
    });
  }, []);

  /** Cancel without producing a blob (no download). */
  const cancelRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      try { recorder.stop(); } catch { /* ignore */ }
    }
    _disconnectAllTaps();
    recordingDestinationRef.current = null;
    mediaRecorderRef.current = null;
    recordedChunksRef.current = [];
    _stopTick();
    setIsRecording(false);
    setRecordingElapsed(0);
    console.log('🎙️ Recording cancelled');
  }, []);

  return {
    // State
    isRecording,
    recordingElapsed,
    formattedDuration: _formatMMSS(recordingElapsed),
    // Methods
    startRecording,
    stopRecording,
    cancelRecording,
    // Refs (escape hatches; rarely needed)
    mediaRecorderRef,
    recordingDestinationRef,
  };
}

export default useRecording;
